import React, {ChangeEvent, Component} from 'react';
import {DraftBooking} from 'services/entities/booking';
import {Product, ProductOption, ProductOptionCode, ProductSKU} from 'services/entities/products';
import ExpandableItem from '../components/ExpandableItem';
import {StorageIcon} from 'components/Icon';
import NumberField from 'components/forms/NumberField';
import ToggleButton from 'components/forms/ToggleButton';
import './StorageCategory.scss';
import {DateTime, TimeSpan} from 'util/time';
import {CurrencyAmount} from 'util/currency';
import {applyCurrencyTaxes} from 'util/pricing';
import {handleError} from 'util/errors';

interface StorageCategoryProps {
  booking: DraftBooking|null;
  products: Product[]|null;
  onProductQuantityChanged:(sku: string, quantity: number)=>void;
  onProductOptionValueChanged:(sku: string, option: string, value: string|null)=>void;
}

interface StorageCategoryState {
  price: CurrencyAmount|null;
}

export default class StorageCategory extends Component<StorageCategoryProps, StorageCategoryState> {
  state: StorageCategoryState = {
    price: null
  };

  componentDidMount(): void {
    if(this.props.products) {
      this.loadProductPrice(this.props.products);
    }
  }

  componentDidUpdate(prevProps: Readonly<StorageCategoryProps>, prevState: Readonly<StorageCategoryState>, snapshot?: any): void {
    if(this.props.products !== prevProps.products && this.props.products) {
      this.loadProductPrice(this.props.products);
    }
  }

  loadProductPrice(products: Product[]) {
    const product = products.find(pr => pr.sku === ProductSKU.StorageBoxes);
    if(!product) {
      return;
    }
    const price = product.prices.find(pr => pr.currency && pr.currency.isDefault);
    if(!price) {
      return;
    }
    applyCurrencyTaxes(price).then(
      totalPrice => this.setState({'price': totalPrice}),
      ex => handleError('Failed loading total price', ex)
    );
  }

  render() {
    const product = this.props.products ? this.props.products.find(pr => pr.sku === ProductSKU.StorageBoxes) : null;
    if(!product) {
      return null;
    }
    const price: CurrencyAmount|null = this.state.price;
    const daysBeforeOption = product.options.find(op => op.code === ProductOptionCode.DaysBefore);
    const daysAfterOption = product.options.find(op => op.code === ProductOptionCode.DaysAfter);
    const bookingProduct = this.props.booking ? this.props.booking.products.find(pr => pr.sku === ProductSKU.StorageBoxes) : null;
    const quantity = bookingProduct ? bookingProduct.quantity : 0;
    const daysBeforeValue = bookingProduct ? bookingProduct.optionValues[ProductOptionCode.DaysBefore] : undefined;
    const daysAfterValue = bookingProduct ? bookingProduct.optionValues[ProductOptionCode.DaysAfter] : undefined;
    const bookingDate = DateTime.now(); //this.props.booking ? this.props.booking.date : null;
    return <ExpandableItem icon={<StorageIcon/>} title={'Storage'} className={'storage'}
                       description={<p>Book storage space up to 3 days prior, and 3 days after your booking.</p>}>
      <h3>{ product.name }</h3>
      { product.description && <div className="description" dangerouslySetInnerHTML={{__html: product.description}} /> }
      { price && <div className="price">{ price!.format("$0") } inc VAT a box, per day</div> }
      <div className="quantity">
        <NumberField minValue={0} maxValue={product.maxQuantity} value={quantity} onChange={this.onQuantityChange} />
        <span className="max-quantity">{ product.maxQuantity } available</span>
      </div>
      <h3>Select the dates you require storage</h3>
      <ul className="dates">
        { daysBeforeOption && this.renderDateOptions(daysBeforeOption, -1, bookingDate, daysBeforeValue) }
        <li><ToggleButton selected={true}>
          { bookingDate ? bookingDate.format('Do MMM') : null }
          <span className="current-note">Your booking</span>
        </ToggleButton></li>
        { daysAfterOption && this.renderDateOptions(daysAfterOption, 1, bookingDate, daysAfterValue) }
      </ul>
    </ExpandableItem>;
  }

  renderDateOptions(option: ProductOption, direction: number, bookingDate: DateTime|null, valueCode: string|undefined) {
    const value = valueCode ? parseInt(valueCode) : 0;
    return option.values.map(v => {
      const daysVal = parseInt(v.code);
      const date = bookingDate ? bookingDate.add(new TimeSpan({days: direction * daysVal})) : null;
      return <li key={v.code}>
        <ToggleButton selected={daysVal <= value} allowDeselect={true}
                      onChange={(selected) => this.onDayToggled(selected, option.code, v.code, direction)}>
          {date ? date.format('Do MMM') : null}
        </ToggleButton>
      </li>
    });
  }

  onQuantityChange = (ev:ChangeEvent<HTMLInputElement>, value: number) => {
    this.props.onProductQuantityChanged(ProductSKU.StorageBoxes, value);
  };

  onDayToggled = (selected:boolean, optionCode: string, valueCode: string, direction: number) => {
    if(selected) {
      this.props.onProductOptionValueChanged(ProductSKU.StorageBoxes, optionCode, valueCode);
    } else {
      const product = this.props.products ? this.props.products.find(pr => pr.sku === ProductSKU.StorageBoxes) : null;
      const option = product ? product.options.find(op => op.code === optionCode) : null;
      if(!option) {
        console.warn("Option not found: " + optionCode);
        return;
      }
      const valueIndex = option.values.findIndex(v => v.code === valueCode);
      const newIndex = valueIndex >= 0 ? valueIndex - direction : -1;
      if(newIndex < 0 || newIndex >= option.values.length) {
        this.props.onProductOptionValueChanged(ProductSKU.StorageBoxes, optionCode, null);
      } else {
        this.props.onProductOptionValueChanged(ProductSKU.StorageBoxes, optionCode, option.values[newIndex].code);
      }
    }
  };
}